import { useMemo } from 'react'

import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { shortenShopifyIdToNumeric } from '../../../../app/_utils/shortenShopifyId'
import { EmbroideryTypeKey } from '../../combination-rules/types'
import { EmbroideryPurpose } from '../../types'
import { useTransientLogo } from '../../useEmbroideryLogos'
import { useNewLogoInOrder } from '../../useNewLogoInOrder'
import { generateEmbroideryLineItemProperties } from '../../utils/generateEmbroideryLineItemProperties'
import { getEmbroideryPriceRange } from '../../utils/getEmbroideryPriceRange'
import { useEmbroideryAvailability } from '../EmbroideryAvailabilityProvider'
import { useEmbroideryGarment } from '../EmbroideryGarmentProvider'
import { useSavedEmbroideryPreferences } from '../EmbroideryPreferencesProvider'
import { useEmbroideryPrices } from '../EmbroideryPricingProvider'
import { useEmbroiderySelection } from '../EmbroiderySelectionProvider'
import { toCatalogTypes } from '../embroideryTypeKeys'
import { ExternalEmbroideryFacade, ExternalEmbroideryFormKind } from '../facade'

import { bagVariantsFromItems } from './bagVariantsFromItems'
import { embroiderySummaryFromItems } from './embroiderySummaryFromItems'
import { legacySelectionsFromItems } from './legacySelectionsFromItems'
import { selectionErrorMessagesFromErrors } from './selectionErrorMessagesFromErrors'

const NO_REQUIRED_TYPES: readonly EmbroideryTypeKey[] = []

/**
 * Fills the external embroidery contract from the new providers, so the parts of a product page
 * outside the modal work unmodified regardless of which stack is mounted.
 *
 * @param requiredTypeKeys - Types the flow makes mandatory. Empty for a shopper on wearfigs.com.
 * @param withPricing - Whether this surface shows prices at all.
 * @param onSelectFormKind - Opens the modal on one kind, or clears the choice of kind.
 * @param onOpenModal - Opens the modal, on the kind last chosen if there is one.
 */
export const useExternalEmbroideryFacadeValue = ({
  embroideryPurpose,
  requiredTypeKeys = NO_REQUIRED_TYPES,
  withPricing,
  onSelectFormKind,
  onOpenModal,
}: {
  embroideryPurpose: EmbroideryPurpose
  requiredTypeKeys?: readonly EmbroideryTypeKey[]
  withPricing: boolean
  onSelectFormKind: (formKind: ExternalEmbroideryFormKind | null) => void
  onOpenModal: () => void
}): ExternalEmbroideryFacade => {
  const { productColorHex, catalogTypeKeys } = useEmbroideryGarment()
  const { savedEmbroideryPreferences, isLoading: isLoadingSavedPreferences } =
    useSavedEmbroideryPreferences()
  const { items, itemsForBag, confirmed, setItemEnabledForBag } = useEmbroiderySelection()
  const { selectionErrors } = useEmbroideryAvailability()
  const { embroideryVariants, digitizationVariant, totalInCentsForItems } = useEmbroideryPrices()
  // Bag lines follow the cart alone; the summary card's prices also count garments on the page.
  const transientLogo = useTransientLogo()
  const newLogoInOrder = useNewLogoInOrder()
  const { locale, region } = useLocalization()
  const translations = useTranslation()

  const { embroideryItemVariant, embroiderySetupVariant } = useMemo(
    () =>
      bagVariantsFromItems({
        items: itemsForBag,
        embroideryVariants,
        embroideryPurpose,
        digitizationVariant,
        transientLogo,
      }),
    [itemsForBag, embroideryVariants, embroideryPurpose, digitizationVariant, transientLogo]
  )

  const embroideryPriceRange = useMemo(
    () => getEmbroideryPriceRange({ embroideryVariants, relatedSetupVariant: undefined, locale }),
    [embroideryVariants, locale]
  )

  const embroideryLineItemProperties = useMemo(() => {
    if (itemsForBag.length === 0) return undefined

    return generateEmbroideryLineItemProperties(
      legacySelectionsFromItems({ items: itemsForBag }),
      productColorHex,
      shortenShopifyIdToNumeric(embroideryItemVariant?.productInfo.shopifyId) ?? undefined,
      shortenShopifyIdToNumeric(embroiderySetupVariant?.productInfo.shopifyId) ?? undefined
    )
  }, [embroideryItemVariant, embroiderySetupVariant, itemsForBag, productColorHex])

  const embroiderySummary = useMemo(
    () =>
      embroiderySummaryFromItems({
        items,
        confirmed,
        hasSavedPreferences: Boolean(savedEmbroideryPreferences),
        locale,
        newLogoInOrder,
        productColorHex,
        region,
        requiredTypeKeys,
        setItemEnabledForBag,
        strings: {
          customize: translations.embroidery.customize,
          editEmbroidery: translations.embroidery.editEmbroidery,
          embroideryItemLabel: translations.cart.embroideryItemLabel,
          toggleAddEmbroidery: translations.embroidery.summary.toggleAddEmbroidery,
          embroideryFormKindTitles: translations.embroidery.embroideryFormKindTitles,
          toggleEmbroiderySelectionAtPlacement:
            translations.embroidery.toggleEmbroiderySelectionAtPlacement,
        },
        totalInCentsForItems,
        withPricing,
      }),
    [
      confirmed,
      items,
      locale,
      newLogoInOrder,
      productColorHex,
      region,
      requiredTypeKeys,
      savedEmbroideryPreferences,
      setItemEnabledForBag,
      totalInCentsForItems,
      translations,
      withPricing,
    ]
  )

  const embroiderySelectionErrorMessages = useMemo(
    () =>
      selectionErrorMessagesFromErrors({
        selectionErrors,
        strings: {
          text: translations.embroidery.validation.textEmbroideryRequired,
          logo: translations.embroidery.validation.logoEmbroideryRequired,
        },
      }),
    [selectionErrors, translations]
  )

  return useMemo(
    () => ({
      productEmbroideryOptionKeys: toCatalogTypes(catalogTypeKeys),
      isProductEmbroiderable: catalogTypeKeys.length > 0,
      requiredEmbroideryTypes: toCatalogTypes(requiredTypeKeys),
      embroideryPriceRange,
      isB2bEmbroidery: embroideryPurpose === 'B2B',

      savedEmbroideryPreferences,
      isLoadingSavedEmbroideryPreferences: isLoadingSavedPreferences,

      confirmAddEmbroidery: confirmed,
      hasEmbroidery: items.length > 0,
      // Business logos are their own flow, and no page this stack serves offers one yet.
      hasB2bLogoSelection: false,
      embroiderySummary,
      embroiderySelectionErrorMessages,

      embroideryItemVariant,
      embroiderySetupVariant,
      embroideryLineItemProperties,

      setSelectedFormKind: onSelectFormKind,
      openEmbroideryModal: onOpenModal,
    }),
    [
      catalogTypeKeys,
      confirmed,
      embroideryItemVariant,
      embroideryLineItemProperties,
      embroideryPriceRange,
      embroideryPurpose,
      embroiderySelectionErrorMessages,
      embroiderySetupVariant,
      embroiderySummary,
      isLoadingSavedPreferences,
      items.length,
      onOpenModal,
      onSelectFormKind,
      requiredTypeKeys,
      savedEmbroideryPreferences,
    ]
  )
}
